import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';
import API from './Api';
import Auth from '../Auth/Auth';
import { useAppTheme } from '../../contexts/ThemeContext';

const SecuritySettings = ({ showNotification }) => {
  const { palette, Colors } = useAppTheme();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [username, setUsername] = useState('');
  const [updatingUsername, setUpdatingUsername] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [roles] = useState([
    { id: 1, name: 'admin',     description: 'Full access to all system features and settings' },
    { id: 2, name: 'manager',   description: 'Can manage properties and tenants' },
    { id: 3, name: 'caretaker', description: 'Access to maintenance and basic property features' }
  ]);

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [submittingUser, setSubmittingUser] = useState(false);

  const [userForm, setUserForm] = useState({
    first_name: '', last_name: '', email: '', username: '',
    password: '', confirmPassword: '', phone_number: '', role_name: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchUserProfile();
    fetchCompanyUsers();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const profileData = await API.auth.getUserProfile();
      setProfile(profileData);
      setUsername(profileData.username || '');
    } catch (err) {
      setError(err.message || 'Failed to load profile');
      showNotification('Failed to load profile. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyUsers = async () => {
    if (!Auth.isAdmin()) return;
    try {
      setLoadingUsers(true);
      const usersData = await API.auth.getCompanyUsers();
      setUsers(usersData.users || []);
    } catch (err) {
      showNotification('Failed to load users. Please try again.', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUsernameUpdate = async () => {
    if (!username.trim()) { showNotification('Username cannot be empty', 'error'); return; }
    try {
      setUpdatingUsername(true);
      await API.auth.updateUsername({ username });
      setProfile(prev => ({ ...prev, username }));
      const currentUser = Auth.getUser();
      if (currentUser) {
        currentUser.username = username;
        Auth.setAuth({ user: currentUser, token: Auth.getToken() });
      }
      showNotification('Username updated successfully');
    } catch (err) {
      showNotification(err.message || 'Failed to update username', 'error');
    } finally {
      setUpdatingUsername(false);
    }
  };

  const handlePasswordUpdate = async () => {
    setPasswordError('');
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setPasswordError('Password must be at least 8 characters'); return; }
    try {
      setUpdatingPassword(true);
      await API.auth.updatePassword({ current_password: currentPassword, new_password: newPassword });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      showNotification('Password updated successfully');
    } catch (err) {
      setPasswordError(err.message || 'Failed to update password');
      showNotification(err.message || 'Failed to update password', 'error');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleTwoFactorToggle = (event) => {
    setTwoFactorEnabled(event.target.checked);
    showNotification(event.target.checked
      ? 'Two-factor authentication enabled'
      : 'Two-factor authentication disabled'
    );
  };

  const handleOpenUserDialog = (userId = null) => {
    if (userId) {
      const user = users.find(u => u.id === userId);
      setUserForm({
        first_name: user.first_name || '', last_name: user.last_name || '',
        email: user.email || '', username: user.username || '',
        password: '', confirmPassword: '',
        phone_number: user.phone_number || '', role_name: user.role || ''
      });
      setEditUserId(userId);
    } else {
      setUserForm({ first_name: '', last_name: '', email: '', username: '', password: '', confirmPassword: '', phone_number: '', role_name: '' });
      setEditUserId(null);
    }
    setFormErrors({});
    setUserDialogOpen(true);
  };

  const handleCloseUserDialog = () => setUserDialogOpen(false);

  const handleUserFormChange = (e) => {
    const { name, value } = e.target;
    setUserForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
  };

  const validateUserForm = () => {
    const errors = {};
    if (!userForm.email && !userForm.username) {
      errors.email = 'Provide at least an email or username';
      errors.username = 'Provide at least an email or username';
    }
    if (!editUserId && !userForm.password) errors.password = 'Password is required';
    if (!editUserId && userForm.password !== userForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    if (!userForm.role_name) errors.role_name = 'Role is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveUser = async () => {
    if (!validateUserForm()) return;
    try {
      setSubmittingUser(true);
      const userData = {
        first_name: userForm.first_name, last_name: userForm.last_name,
        email: userForm.email, username: userForm.username,
        phone_number: userForm.phone_number, role_name: userForm.role_name
      };
      if (userForm.password) userData.password = userForm.password;
      if (editUserId) {
        await API.auth.updateUser(editUserId, userData);
        showNotification('User updated successfully');
      } else {
        await API.auth.createUser(userData);
        showNotification('User created successfully');
      }
      fetchCompanyUsers();
      setUserDialogOpen(false);
    } catch (err) {
      showNotification(err.message || 'Failed to save user', 'error');
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleDeleteUserConfirm = (userId) => { setDeleteUserId(userId); setConfirmDialogOpen(true); };

  const handleDeleteUser = async () => {
    try {
      await API.auth.deleteUser(deleteUserId);
      setUsers(users.filter(u => u.id !== deleteUserId));
      setConfirmDialogOpen(false);
      showNotification('User removed successfully');
    } catch (err) {
      showNotification(err.message || 'Failed to delete user', 'error');
    }
  };

  const handleSignOutAllDevices = async () => {
    try {
      await API.auth.signOutAllDevices();
      showNotification('All devices have been signed out successfully');
      setTimeout(() => { Auth.clearAuth(); window.location.href = '/login'; }, 2000);
    } catch (err) {
      showNotification(err.message || 'Failed to sign out all devices', 'error');
    }
  };

  const isAdmin = Auth.isAdmin();

  // ── Shared style helpers ──────────────────────────────────────
  const paperSx = {
    p: 3, mb: 3,
    backgroundColor: palette.background.tertiary,
    border: `1px solid ${palette.border}`,
    boxShadow: 'none',
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: palette.border },
      '&:hover fieldset': { borderColor: Colors.primary.main },
      backgroundColor: palette.background.secondary,
    },
    '& .MuiInputLabel-root': { color: palette.text.secondary },
    '& .MuiInputBase-input': { color: palette.text.primary },
  };

  const primaryBtnSx = {
    bgcolor: Colors.primary.main,
    '&:hover': { bgcolor: Colors.primary.dark },
    '&.Mui-disabled': { bgcolor: palette.background.secondary },
  };

  const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: Colors.primary.main },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: Colors.primary.main },
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':     return Colors.status.error;
      case 'manager':   return Colors.primary.main;
      case 'caretaker': return Colors.accent.blue;
      default:          return palette.text.muted;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', m: 3 }}>
        <CircularProgress sx={{ color: Colors.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={paperSx}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: palette.text.primary }}>
        Security &amp; Privacy
      </Typography>

      {/* Username */}
      <Paper sx={paperSx}>
        <Typography variant="h6" gutterBottom sx={{ color: palette.text.primary }}>Username</Typography>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth margin="normal" label="Username" value={username}
            onChange={(e) => setUsername(e.target.value)}
            helperText="Set a username that can be used for login (instead of email)"
            required sx={fieldSx}
            FormHelperTextProps={{ sx: { color: palette.text.muted } }}
          />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleUsernameUpdate}
              disabled={updatingUsername || !username} sx={primaryBtnSx}>
              {updatingUsername ? <CircularProgress size={24} color="inherit" /> : 'Update Username'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Change Password */}
      <Paper sx={paperSx}>
        <Typography variant="h6" gutterBottom sx={{ color: palette.text.primary }}>Change Password</Typography>
        <Box sx={{ mt: 2 }}>
          <TextField fullWidth margin="normal" label="Current Password" type="password"
            value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required sx={fieldSx} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth margin="normal" label="New Password" type="password"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                required error={!!passwordError} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth margin="normal" label="Confirm New Password" type="password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                required error={!!passwordError} helperText={passwordError} sx={fieldSx}
                FormHelperTextProps={{ sx: { color: Colors.status.error } }} />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handlePasswordUpdate}
              disabled={updatingPassword || !currentPassword || !newPassword || !confirmPassword}
              sx={primaryBtnSx}>
              {updatingPassword ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Two-Factor Auth */}
      <Paper sx={paperSx}>
        <Typography variant="h6" gutterBottom sx={{ color: palette.text.primary }}>
          Two-Factor Authentication
        </Typography>
        <FormControlLabel
          control={<Switch checked={twoFactorEnabled} onChange={handleTwoFactorToggle} sx={switchSx} />}
          label={<Typography sx={{ color: palette.text.primary }}>Enable Two-Factor Authentication</Typography>}
        />
        <Typography variant="body2" sx={{ mt: 1, color: palette.text.secondary }}>
          Add an extra layer of security to your account by requiring a verification code in addition to your password.
        </Typography>
      </Paper>

      {/* Users & Roles — admin only */}
      {isAdmin && (
        <Paper sx={paperSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: palette.text.primary }}>Users &amp; Roles</Typography>
            <Button
              variant="contained" startIcon={<AddIcon />}
              onClick={() => handleOpenUserDialog()}
              sx={primaryBtnSx}
            >
              Add User
            </Button>
          </Box>

          <TableContainer>
            <Table aria-label="users table">
              <TableHead>
                <TableRow>
                  {['Name', 'Email', 'Username', 'Phone Number', 'Role', 'Last Login', 'Actions'].map((h) => (
                    <TableCell key={h} sx={{ color: palette.text.secondary, borderColor: palette.border, fontWeight: 600 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingUsers ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ borderColor: palette.border }}>
                      <CircularProgress size={24} sx={{ color: Colors.primary.main }} />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ color: palette.text.secondary, borderColor: palette.border }}>
                      No users found
                    </TableCell>
                  </TableRow>
                ) : users.map((user) => (
                  <TableRow key={user.id} sx={{ '&:hover': { backgroundColor: palette.background.primary } }}>
                    <TableCell sx={{ color: palette.text.primary, borderColor: palette.border }}>
                      {`${user.first_name || ''} ${user.last_name || ''}`}
                    </TableCell>
                    <TableCell sx={{ color: palette.text.primary, borderColor: palette.border }}>{user.email}</TableCell>
                    <TableCell sx={{ color: palette.text.secondary, borderColor: palette.border }}>{user.username || '-'}</TableCell>
                    <TableCell sx={{ color: palette.text.secondary, borderColor: palette.border }}>{user.phone_number || '-'}</TableCell>
                    <TableCell sx={{ borderColor: palette.border }}>
                      <Chip
                        label={user.role}
                        size="small"
                        sx={{
                          bgcolor: `${getRoleColor(user.role)}20`,
                          color: getRoleColor(user.role),
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: palette.text.secondary, borderColor: palette.border }}>
                      {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: palette.border }}>
                      <Button size="small" startIcon={<EditIcon />}
                        onClick={() => handleOpenUserDialog(user.id)}
                        sx={{ color: Colors.primary.main }}>
                        Edit
                      </Button>
                      {user.role !== 'admin' && (
                        <Button size="small" startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteUserConfirm(user.id)}
                          sx={{ color: Colors.status.error }}>
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Session Management */}
      <Paper sx={paperSx}>
        <Typography variant="h6" gutterBottom sx={{ color: palette.text.primary }}>
          Session Management
        </Typography>
        <Button
          variant="outlined" startIcon={<LogoutIcon />} onClick={handleSignOutAllDevices}
          sx={{ borderColor: Colors.status.error, color: Colors.status.error,
            '&:hover': { borderColor: Colors.status.error, backgroundColor: `${Colors.status.error}10` } }}
        >
          Sign Out All Devices
        </Button>
        <Typography variant="body2" sx={{ mt: 1, color: palette.text.secondary }}>
          This will log out all devices that are currently signed in with your account, including this one.
        </Typography>
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog open={userDialogOpen} onClose={handleCloseUserDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { backgroundColor: palette.background.secondary, color: palette.text.primary } }}>
        <DialogTitle sx={{ color: palette.text.primary, borderBottom: `1px solid ${palette.border}` }}>
          {editUserId ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField autoFocus margin="dense" name="first_name" label="First Name" type="text"
                fullWidth value={userForm.first_name} onChange={handleUserFormChange} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField margin="dense" name="last_name" label="Last Name" type="text"
                fullWidth value={userForm.last_name} onChange={handleUserFormChange} sx={fieldSx} />
            </Grid>
          </Grid>
          <TextField
            margin="dense" name="email" label="Email Address" type="email" fullWidth
            value={userForm.email} onChange={handleUserFormChange}
            error={!!formErrors.email} helperText={formErrors.email || 'Optional if username is provided'}
            sx={fieldSx}
          />
          <TextField
            margin="dense" name="username" label="Username" type="text" fullWidth
            value={userForm.username} onChange={handleUserFormChange}
            error={!!formErrors.username} helperText={formErrors.username || 'Optional if email is provided'}
            sx={fieldSx}
          />
          <TextField margin="dense" name="phone_number" label="Phone Number" type="tel" fullWidth
            value={userForm.phone_number} onChange={handleUserFormChange} sx={fieldSx} />
          {!editUserId && (
            <>
              <TextField margin="dense" name="password" label="Password" type="password" fullWidth
                value={userForm.password} onChange={handleUserFormChange} required
                error={!!formErrors.password} helperText={formErrors.password} sx={fieldSx} />
              <TextField margin="dense" name="confirmPassword" label="Confirm Password" type="password" fullWidth
                value={userForm.confirmPassword} onChange={handleUserFormChange} required
                error={!!formErrors.confirmPassword} helperText={formErrors.confirmPassword} sx={fieldSx} />
            </>
          )}
          {editUserId && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, mb: 2, color: palette.text.muted }}>
              Leave password fields empty to keep current password
            </Typography>
          )}
          <FormControl fullWidth margin="dense" error={!!formErrors.role_name} sx={fieldSx}>
            <InputLabel>Role</InputLabel>
            <Select name="role_name" value={userForm.role_name} onChange={handleUserFormChange} required label="Role">
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.name}>
                  {role.name.charAt(0).toUpperCase() + role.name.slice(1)} — {role.description}
                </MenuItem>
              ))}
            </Select>
            {formErrors.role_name && (
              <Typography variant="caption" sx={{ color: Colors.status.error }}>{formErrors.role_name}</Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${palette.border}`, px: 3, py: 2 }}>
          <Button onClick={handleCloseUserDialog} sx={{ color: palette.text.secondary }}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained" disabled={submittingUser} sx={primaryBtnSx}>
            {submittingUser ? <CircularProgress size={24} color="inherit" /> : (editUserId ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}
        PaperProps={{ sx: { backgroundColor: palette.background.secondary, color: palette.text.primary } }}>
        <DialogTitle sx={{ color: palette.text.primary }}>Confirm User Removal</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: palette.text.secondary }}>
            Are you sure you want to remove this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${palette.border}`, px: 3, py: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} sx={{ color: palette.text.secondary }}>Cancel</Button>
          <Button onClick={handleDeleteUser} variant="contained"
            sx={{ bgcolor: Colors.status.error, '&:hover': { bgcolor: '#dc2626' } }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecuritySettings;